// useQuizPersistence.js - Hook para persistir el estado del quiz
import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'mediexampro_quiz_state';
const AUTOSAVE_INTERVAL = 5000; // Guardar cada 5 segundos

/**
 * Hook personalizado para persistir el estado del quiz
 * Guarda automáticamente el progreso y lo restaura al volver
 */
export const useQuizPersistence = (quizId, initialState = {}) => {
  const [isRestoring, setIsRestoring] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const autosaveTimer = useRef(null);
  const stateRef = useRef(initialState);

  // Generar clave única para este quiz específico
  const getStorageKey = useCallback(() => {
    return `${STORAGE_KEY}_${quizId || 'default'}`;
  }, [quizId]);

  // Guardar estado en localStorage
  const saveState = useCallback((state) => {
    try {
      const storageKey = getStorageKey();
      const dataToSave = {
        ...state,
        timestamp: Date.now(),
        quizId: quizId
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      // Notificar a la app que el estado del quiz cambió (para barras globales)
      try {
        window.dispatchEvent(new CustomEvent('mediexam_quiz_state', { detail: { quizId, storageKey } }));
      } catch (_) {}
      setLastSaved(new Date());
      console.log('✅ Estado del quiz guardado:', {
        quizId,
        currentQuestion: state.currentQuestion,
        correctAnswers: state.correctAnswersCount,
        timestamp: new Date().toLocaleTimeString()
      });
      
      return true;
    } catch (error) {
      console.error('❌ Error guardando estado del quiz:', error);
      return false;
    }
  }, [quizId, getStorageKey]);

  // Restaurar estado desde localStorage
  const restoreState = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const savedData = localStorage.getItem(storageKey);
      
      if (!savedData) {
        console.log('📭 No hay estado guardado para este quiz');
        setIsRestoring(false);
        return null;
      }

      const parsedData = JSON.parse(savedData);
      
      // Verificar si el estado guardado no es muy antiguo (24 horas)
      const age = Date.now() - parsedData.timestamp;
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 días
      
      if (age > maxAge) {
        console.log('⏰ Estado guardado muy antiguo, ignorando');
        localStorage.removeItem(storageKey);
        setIsRestoring(false);
        return null;
      }

      console.log('✅ Estado del quiz restaurado:', {
        quizId: parsedData.quizId,
        currentQuestion: parsedData.currentQuestion,
        correctAnswers: parsedData.correctAnswersCount,
        age: Math.round(age / 1000 / 60) + ' minutos'
      });

      setIsRestoring(false);
      return parsedData;
    } catch (error) {
      console.error('❌ Error restaurando estado del quiz:', error);
      setIsRestoring(false);
      return null;
    }
  }, [getStorageKey]);

  // Limpiar estado guardado
  const clearSavedState = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      console.log('🗑️ Estado del quiz limpiado');
      try {
        window.dispatchEvent(new CustomEvent('mediexam_quiz_state', { detail: { quizId, storageKey } }));
      } catch (_) {}
      return true;
    } catch (error) {
      console.error('❌ Error limpiando estado del quiz:', error);
      return false;
    }
  }, [getStorageKey]);

  // Auto-guardar periódicamente
  const scheduleAutosave = useCallback((state) => {
    // Cancelar autosave anterior
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
    }

    // Programar nuevo autosave
    autosaveTimer.current = setTimeout(() => {
      saveState(state);
    }, AUTOSAVE_INTERVAL);
  }, [saveState]);

  // Cancelar cualquier autosave pendiente (útil al finalizar simulacro)
  const cancelAutosave = useCallback(() => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
  }, []);

  // Guardar estado inmediatamente (para eventos importantes)
  const saveStateImmediate = useCallback((state) => {
    // Cancelar autosave pendiente
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
    }
    
    // Guardar inmediatamente
    saveState(state);
    
    // Reprogramar autosave
    scheduleAutosave(state);
  }, [saveState, scheduleAutosave]);

  // Guardar al salir de la página o cambiar de pestaña
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (stateRef.current) {
        saveState(stateRef.current);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && stateRef.current) {
        // Guardar cuando la pestaña se oculta
        saveState(stateRef.current);
      }
    };

    // Agregar listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Limpiar al desmontar
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Cancelar autosave pendiente
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, [saveState]);

  // Actualizar referencia del estado actual
  const updateStateRef = useCallback((state) => {
    stateRef.current = state;
  }, []);

  return {
    saveState,
    saveStateImmediate,
    restoreState,
    clearSavedState,
    scheduleAutosave,
    updateStateRef,
    isRestoring,
    lastSaved,
    cancelAutosave
  };
};

/**
 * Hook para crear un estado persistente
 * Similar a useState pero con persistencia automática
 */
export const usePersistentState = (key, initialValue, quizId = 'default') => {
  const storageKey = `${STORAGE_KEY}_${quizId}_${key}`;
  
  // Inicializar con valor guardado o valor inicial
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        console.log(`✅ Restaurado ${key}:`, parsed.value);
        return parsed.value;
      }
    } catch (error) {
      console.error(`Error restaurando ${key}:`, error);
    }
    return initialValue;
  });

  // Guardar cuando cambie el estado
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        value: state,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error(`Error guardando ${key}:`, error);
    }
  }, [state, storageKey, key]);

  // Función para limpiar el estado persistente
  const clearPersistedState = useCallback(() => {
    localStorage.removeItem(storageKey);
    setState(initialValue);
  }, [storageKey, initialValue]);

  return [state, setState, clearPersistedState];
};

export default useQuizPersistence;
